import { useCMS } from '../../contexts/CMSContext';
import EditableText from '../cms/EditableText';
import { Link } from 'react-router-dom';
import { getEffectiveField, getEffectiveServiceItem } from '../../utils/cmsMobile';

const LOGO_URL = 'https://vhnuplacedgmgryjrjhn.supabase.co/storage/v1/object/public/cms-images/logo_dario_trans.png';

export default function Footer() {
  const { data, isEditMode, isMobileContentActive } = useCMS();
  const { footer } = data;
  const emailEffective = getEffectiveField(data, 'footer', 'email', isMobileContentActive);
  const phoneEffective = getEffectiveField(data, 'footer', 'phone', isMobileContentActive);
  const phoneForDial = phoneEffective.replace(/[^\d+]/g, '');
  const normalizedPhoneForDial = phoneForDial.startsWith('+')
    ? phoneForDial
    : `+34${phoneForDial}`;
  const phoneHref = `tel:${normalizedPhoneForDial}`;
  const emailHref = `mailto:${emailEffective}`;

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  const link = (field: `footerLink${1 | 2 | 3 | 4 | 5}`) =>
    getEffectiveField(data, 'seo', field, isMobileContentActive);

  return (
    <footer className="bg-[#EDE8E1]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl p-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <img src={LOGO_URL} alt="Logo" className="h-20 w-auto mb-4 object-contain" />
            <EditableText
              section="footer"
              field="description"
              tag="p"
              multiline
              className="text-[#6B6B6B] text-sm leading-relaxed"
            />
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A] mb-4">Servicios</h4>
            <ul className="flex flex-col gap-3">
              {data.services.items.slice(0, 4).map((svc) => {
                const title = getEffectiveServiceItem(data, svc.id, isMobileContentActive)?.title ?? svc.title;
                return (
                  <li key={svc.id}>
                    <a
                      href="#servicios"
                      onClick={(e) => handleNavClick(e, '#servicios')}
                      className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors cursor-pointer"
                      rel="nofollow"
                    >
                      {title}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A] mb-4">Contacto</h4>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-[#8B9D83] text-base"></i>
                </div>
                {isEditMode ? (
                  <EditableText section="footer" field="email" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <a
                    href={emailHref}
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {emailEffective}
                  </a>
                )}
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-[#8B9D83] text-base"></i>
                </div>
                {isEditMode ? (
                  <EditableText section="footer" field="phone" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <a
                    href={phoneHref}
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {phoneEffective}
                  </a>
                )}
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-time-line text-[#8B9D83] text-base"></i>
                </div>
                <EditableText section="footer" field="schedule" tag="span" className="text-sm text-[#6B6B6B]" />
              </li>
            </ul>
          </div>

          <div>
            <EditableText
              section="seo"
              field="footerLinksTitle"
              tag="h4"
              className="text-xs font-bold uppercase tracking-widest text-[#1A1A1A] mb-4"
            />
            <ul className="flex flex-col gap-3">
              <li>
                {isEditMode ? (
                  <EditableText section="seo" field="footerLink1" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <Link
                    to="/nutricionista-palma"
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {link('footerLink1')}
                  </Link>
                )}
              </li>
              <li>
                {isEditMode ? (
                  <EditableText section="seo" field="footerLink2" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <Link
                    to="/dietista-palma-de-mallorca"
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {link('footerLink2')}
                  </Link>
                )}
              </li>
              <li>
                {isEditMode ? (
                  <EditableText section="seo" field="footerLink3" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <Link
                    to="/nutricionista-clinico-palma-de-mallorca"
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {link('footerLink3')}
                  </Link>
                )}
              </li>
              <li>
                {isEditMode ? (
                  <EditableText section="seo" field="footerLink4" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <Link
                    to="/adelgazar-palma-de-mallorca"
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {link('footerLink4')}
                  </Link>
                )}
              </li>
              <li>
                {isEditMode ? (
                  <EditableText section="seo" field="footerLink5" tag="span" className="text-sm text-[#6B6B6B]" />
                ) : (
                  <Link
                    to="/nutricion-deportiva-palma-de-mallorca"
                    className="text-sm text-[#6B6B6B] hover:text-[#8B9D83] transition-colors"
                  >
                    {link('footerLink5')}
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-3 px-2">
          <EditableText section="footer" field="copyright" tag="p" className="text-xs text-[#6B6B6B]" />
          <div className="flex gap-4 text-xs text-[#6B6B6B]">
            <a href="#" rel="nofollow" className="hover:text-[#8B9D83] transition-colors cursor-pointer">
              Privacidad
            </a>
            <span>|</span>
            <a href="#" rel="nofollow" className="hover:text-[#8B9D83] transition-colors cursor-pointer">
              Términos
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
